/**
 * 時間前市場 server-stream 購読 hook。
 *
 * NotifyMarket (Connect-Web server-streaming RPC) を購読し、
 * event 受信時に選択中コマに一致する unary query を invalidate する。
 *
 * - 自動更新 ON 時のみ購読開始、OFF で中断。
 * - stream 切断時は backoff (1s→2s→4s…, cap 16s) で reconnect。
 * - 接続バッジ (LIVE / 再接続中… / OFF) と 「更新停止中」 の表示判定 (streamStopped) を、
 *   購読の段階 (StreamPhase) 1 つから返す。
 *
 * React Query では server-streaming RPC を直接扱えないため、
 * useEffect 内で Connect-Web client の async iterable を回す実装。
 */
import { useCallback, useEffect, useRef, useState } from 'react'

import { create } from '@bufbuild/protobuf'
import { createClient } from '@connectrpc/connect'
import { createConnectQueryKey, useTransport } from '@connectrpc/connect-query'
import {
  listBiddingInterconnectionCapacities,
  listBiddingMarketContracts,
  listBiddingMarketOrderBook,
} from '@ebalance/proto/bffuser/v1/bidding_intraday_market_service-BiddingIntradayMarketService_connectquery'
import {
  BiddingIntradayMarketService,
  NotifyMarketRequestSchema,
} from '@ebalance/proto/bffuser/v1/bidding_intraday_market_service_pb'
import type { Date as ProtoDate } from '@ebalance/proto/google/type/date_pb'
import type { QueryClient } from '@tanstack/react-query'
import { useQueryClient } from '@tanstack/react-query'

import {
  STREAM_BACKOFF_CAP_MS,
  STREAM_BACKOFF_INITIAL_MS,
  STREAM_BACKOFF_MULTIPLIER,
} from '~/pages/IntradayBidding/constants/market'

/**
 * 購読の段階。 'idle' = 自動更新 OFF。 'reconnecting' = 一度切れて、 再接続の待ち時間・試行中
 * (応答待ちを含む)。 最初の 1 通を受信するまで 'reconnecting' のままで、 'connecting' に戻らない。
 */
type StreamPhase = 'idle' | 'connecting' | 'connected' | 'reconnecting'

type UseMarketNotificationStreamResult = {
  phase: StreamPhase
  /**
   * 切断してから再接続して最初の 1 通を受信するまで true (= 「更新停止中」 の表示判定)。
   * 再接続の試行中も true のままにして、 再試行のたびに消えない・応答待ちで出ない、 を
   * 避ける。 初回の接続中・自動更新 OFF・unmount では false。
   */
  streamStopped: boolean
}

/**
 * proto Date と JS Date (local midnight) を比較する。
 * deliveryDate が同じ年月日かどうかをチェック。
 */
function protoDateMatches(
  proto: ProtoDate | undefined,
  jsDate: Date | undefined,
): boolean {
  if (proto === undefined || jsDate === undefined) return false
  return (
    proto.year === jsDate.getFullYear() &&
    proto.month === jsDate.getMonth() + 1 &&
    proto.day === jsDate.getDate()
  )
}

function invalidateMarketQueries(
  queryClient: QueryClient,
  deliveryDate: Date | undefined,
  timeCode: number,
  includeIntercon: boolean,
): void {
  if (deliveryDate === undefined || timeCode <= 0) return

  const protoDeliveryDate: ProtoDate = {
    $typeName: 'google.type.Date',
    year: deliveryDate.getFullYear(),
    month: deliveryDate.getMonth() + 1,
    day: deliveryDate.getDate(),
  }

  // 板 invalidate
  void queryClient.invalidateQueries({
    queryKey: createConnectQueryKey({
      schema: listBiddingMarketOrderBook,
      cardinality: 'finite',
      input: { deliveryDate: protoDeliveryDate, timeCode },
    }),
  })

  // 約定 invalidate
  void queryClient.invalidateQueries({
    queryKey: createConnectQueryKey({
      schema: listBiddingMarketContracts,
      cardinality: 'finite',
      input: { deliveryDate: protoDeliveryDate, timeCode },
    }),
  })

  // 連系線 (= コマ非依存、 interconnectionLinesUpdated event 時のみ)
  if (includeIntercon) {
    void queryClient.invalidateQueries({
      queryKey: createConnectQueryKey({
        schema: listBiddingInterconnectionCapacities,
        cardinality: 'finite',
        input: { deliveryDate: protoDeliveryDate, timeCode },
      }),
    })
  }
}

export function useMarketNotificationStream(
  autoRefresh: boolean,
  selectedDeliveryDate: Date | undefined,
  selectedTimeCode: number,
): UseMarketNotificationStreamResult {
  const transport = useTransport()
  const queryClient = useQueryClient()
  const [phase, setPhase] = useState<StreamPhase>('idle')

  // backoff state は ref で管理 (= render を起こさない)
  const backoffRef = useRef(STREAM_BACKOFF_INITIAL_MS)
  const abortControllerRef = useRef<AbortController | null>(null)
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // 選択中コマ / 受渡日は stream の event filter にしか使わない (= request は
  // parameterless で全 event を受ける)。 これらを useEffect 依存に入れると
  // コマ / 日付クリックのたびに stream が切断→再接続し、 LIVE→再接続中… の
  // ちらつきと再接続窓での event 取りこぼしが起きる。 ref に同期保持して loop
  // 内で最新値を読み、 依存配列からは外す。
  const selectedDeliveryDateRef = useRef(selectedDeliveryDate)
  const selectedTimeCodeRef = useRef(selectedTimeCode)
  selectedDeliveryDateRef.current = selectedDeliveryDate
  selectedTimeCodeRef.current = selectedTimeCode

  const stopStream = useCallback(() => {
    if (reconnectTimerRef.current !== null) {
      clearTimeout(reconnectTimerRef.current)
      reconnectTimerRef.current = null
    }
    if (abortControllerRef.current !== null) {
      abortControllerRef.current.abort()
      abortControllerRef.current = null
    }
    setPhase('idle')
  }, [])

  useEffect(() => {
    if (!autoRefresh) {
      stopStream()
      return
    }

    let isMounted = true
    backoffRef.current = STREAM_BACKOFF_INITIAL_MS

    // 切れた stream の再接続を backoff 後に予約する (エラー終了・server 側終了で共通)。
    const scheduleReconnect = (): void => {
      setPhase('reconnecting')
      const backoff = backoffRef.current
      backoffRef.current = Math.min(
        backoff * STREAM_BACKOFF_MULTIPLIER,
        STREAM_BACKOFF_CAP_MS,
      )
      reconnectTimerRef.current = setTimeout(() => {
        if (isMounted) {
          void startStream()
        }
      }, backoff)
    }

    const startStream = async (): Promise<void> => {
      if (!isMounted) return

      const ac = new AbortController()
      abortControllerRef.current = ac
      // 再接続の試行中は 'reconnecting' のまま (= 最初の 1 通を受信するまで戻さない)。
      setPhase((prev) => (prev === 'reconnecting' ? prev : 'connecting'))

      const client = createClient(BiddingIntradayMarketService, transport)

      try {
        const stream = client.notifyMarket(
          create(NotifyMarketRequestSchema, {}),
          { signal: ac.signal },
        )

        // connected 遷移 / backoff リセットは「最初の 1 通を受信」 後に行う。
        // notifyMarket() は async iterable を即時返すだけ (HTTP は初回 iterate
        // で遅延発火) なので、 ここより前で reset すると network エラー観測前に
        // 毎回 backoff が初期値へ戻り、 指数 backoff (1s→2s→…16s) が効かず連続
        // 失敗時に毎回 1s で再接続を flood する。 1 通受信 = 接続生存の証跡。
        let firstMessageReceived = false

        for await (const response of stream) {
          if (!isMounted) break

          if (!firstMessageReceived) {
            firstMessageReceived = true
            setPhase('connected')
            backoffRef.current = STREAM_BACKOFF_INITIAL_MS
          }

          if (response.data.case !== 'payload') {
            // keepAlive = 接続維持のみ、 無視
            continue
          }

          const payload = response.data.value
          const event = payload.event

          // ref から最新の選択中コマ / 受渡日を読む (= stream は再接続せず、
          // filter のみ常に最新の user 選択に追従させる)。
          const currentTimeCode = selectedTimeCodeRef.current
          const currentDeliveryDate = selectedDeliveryDateRef.current

          if (event.case === 'marketBidUpdated') {
            // 板更新: 選択中コマに一致する場合のみ refetch
            for (const ddt of event.value.deliveryDateTimes) {
              if (
                ddt.timeCode === currentTimeCode &&
                protoDateMatches(ddt.deliveryDate, currentDeliveryDate)
              ) {
                invalidateMarketQueries(
                  queryClient,
                  currentDeliveryDate,
                  currentTimeCode,
                  false,
                )
                break
              }
            }
          } else if (event.case === 'marketContractUpdated') {
            // 板 / ティッカーは選択中コマに一致する場合のみ refetch
            for (const ddt of event.value.deliveryDateTimes) {
              if (
                ddt.timeCode === currentTimeCode &&
                protoDateMatches(ddt.deliveryDate, currentDeliveryDate)
              ) {
                invalidateMarketQueries(
                  queryClient,
                  currentDeliveryDate,
                  currentTimeCode,
                  false,
                )
                break
              }
            }
          } else if (event.case === 'interconnectionLinesUpdated') {
            // 連系線更新: コマ非依存。 常に refetch
            invalidateMarketQueries(
              queryClient,
              currentDeliveryDate,
              currentTimeCode,
              true,
            )
          } else if (
            event.case === 'gatePreClose' ||
            event.case === 'gateClosed'
          ) {
            // GC 通知: 対象コマの板・約定を refetch
            const ddtList =
              event.case === 'gatePreClose'
                ? event.value.deliveryDateTimes
                : event.value.deliveryDateTimes
            for (const ddt of ddtList) {
              if (
                ddt.timeCode === currentTimeCode &&
                protoDateMatches(ddt.deliveryDate, currentDeliveryDate)
              ) {
                invalidateMarketQueries(
                  queryClient,
                  currentDeliveryDate,
                  currentTimeCode,
                  false,
                )
                break
              }
            }
          }
        }
      } catch (err) {
        if (!isMounted) return
        // abort は正常終了 (= autoRefresh OFF / unmount)
        if (ac.signal.aborted) return
        // 予期しないエラー → backoff reconnect
        scheduleReconnect()
        return
      }

      // 正常終了 (= stream が server 側から閉じられた) → reconnect
      if (!isMounted) return
      scheduleReconnect()
    }

    void startStream()

    return () => {
      isMounted = false
      stopStream()
    }
    // selectedDeliveryDate / selectedTimeCode は依存に含めない (= ref 経由で
    // loop 内が最新値を読むため)。 含めるとコマ / 日付クリックで stream が
    // 切断→再接続し、 再接続窓で event を取りこぼす。
  }, [autoRefresh, transport, queryClient, stopStream])

  // 自動更新を切り替えた render は effect が段階を書き換える前なので、 ON / OFF から補う。
  let currentPhase = phase
  if (!autoRefresh) {
    currentPhase = 'idle'
  } else if (phase === 'idle') {
    currentPhase = 'connecting'
  }

  return {
    phase: currentPhase,
    streamStopped: currentPhase === 'reconnecting',
  }
}
